import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { TemplateRubric, TemplateRubricField } from "schemas/admin";

import { TemplateRubricName } from "features/Influencer/ContactList/schema";

import { RubricNameToDescription } from "./helpers";

const EMPTY_NEW_FIELD = { name: "", label: "", description: "", score: 0 };

type SortableFieldRowProps = {
  field: TemplateRubricField;
  index: number;
  saving: boolean;
  onFieldChange: (
    index: number,
    key: "description" | "score",
    value: string,
  ) => void;
  onDeleteClick: (index: number) => void;
};

const SortableFieldRow = ({
  field,
  index,
  saving,
  onFieldChange,
  onDeleteClick,
}: SortableFieldRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.name });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        bgcolor: isDragging ? "action.hover" : "transparent",
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            mt: 3.5,
            cursor: "grab",
            color: "text.disabled",
            "&:active": { cursor: "grabbing" },
          }}
          {...attributes}
          {...listeners}
        >
          <Box component="i" className="fa-regular fa-grip-dots-vertical" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {field.label}
          </Typography>
          <TextField
            fullWidth
            multiline
            size="small"
            value={field.description}
            onChange={(e) =>
              onFieldChange(index, "description", e.target.value)
            }
          />
        </Box>
        <Box sx={{ width: 100, flexShrink: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            &nbsp;
          </Typography>
          <TextField
            size="small"
            label="Max Score"
            type="number"
            value={field.score}
            onChange={(e) => onFieldChange(index, "score", e.target.value)}
            inputProps={{ min: 0 }}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            &nbsp;
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(index);
            }}
            disabled={saving}
            color="error"
          >
            <Box component="i" className="fa-regular fa-trash" />
          </IconButton>
        </Box>
      </Stack>
    </Box>
  );
};

type Props = {
  rubric: TemplateRubric;
  onSave: (rubricId: number, fields: TemplateRubricField[]) => Promise<void>;
  saving: boolean;
};

const RubricCard = ({ rubric, onSave, saving }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [fields, setFields] = useState<TemplateRubricField[]>(rubric.fields);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newField, setNewField] =
    useState<TemplateRubricField>(EMPTY_NEW_FIELD);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFields((prev) => {
      const oldIndex = prev.findIndex((f) => f.name === active.id);
      const newIndex = prev.findIndex((f) => f.name === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleFieldChange = (
    index: number,
    key: "description" | "score",
    value: string,
  ) => {
    setFields((prev) =>
      prev.map((f, i) =>
        i === index
          ? { ...f, [key]: key === "score" ? Number(value) : value }
          : f,
      ),
    );
  };

  const handleDeleteConfirm = async () => {
    if (deleteIndex === null) return;
    const newFields = fields.filter((_, i) => i !== deleteIndex);
    setDeleteIndex(null);
    setFields(newFields);
    await onSave(rubric.id, newFields);
  };

  const handleAddConfirm = async () => {
    const newFields = [...fields, newField];
    setAddDialogOpen(false);
    setNewField(EMPTY_NEW_FIELD);
    setFields(newFields);
    await onSave(rubric.id, newFields);
  };

  const handleAddClose = () => {
    setAddDialogOpen(false);
    setNewField(EMPTY_NEW_FIELD);
  };

  const isNewFieldValid =
    newField.name.trim() !== "" &&
    newField.label.trim() !== "" &&
    newField.description.trim() !== "";

  const isDirty = JSON.stringify(fields) !== JSON.stringify(rubric.fields);

  return (
    <>
      <Card variant="outlined" sx={{ maxWidth: 1200 }}>
        <CardHeader
          title={
            <Typography variant="h6">
              <strong>
                {RubricNameToDescription[rubric.name as TemplateRubricName]}
              </strong>{" "}
              (Total score possible:{" "}
              {fields.reduce((sum, f) => sum + f.score, 0)})
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                size="small"
                disabled={!isDirty || saving}
                onClick={() => onSave(rubric.id, fields)}
              >
                Save
              </Button>
              <IconButton
                size="small"
                onClick={() => setExpanded((prev) => !prev)}
              >
                <Box
                  component="i"
                  className={`fa-regular ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`}
                />
              </IconButton>
            </Stack>
          }
          sx={{ cursor: "pointer" }}
          onClick={() => setExpanded((prev) => !prev)}
        />
        <Collapse in={expanded}>
          <Divider />
          <CardContent sx={{ p: 0 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map((f) => f.name)}
                strategy={verticalListSortingStrategy}
              >
                {fields.map((field, index) => (
                  <SortableFieldRow
                    key={field.name}
                    field={field}
                    index={index}
                    saving={saving}
                    onFieldChange={handleFieldChange}
                    onDeleteClick={setDeleteIndex}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 2, py: 1 }}>
            <Button
              size="small"
              startIcon={<Box component="i" className="fa-regular fa-plus" />}
              onClick={(e) => {
                e.stopPropagation();
                setAddDialogOpen(true);
              }}
              disabled={saving}
            >
              Add Field
            </Button>
          </CardActions>
        </Collapse>
      </Card>

      <Dialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)}>
        <DialogTitle>Remove field</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this field from the rubric? It will
            affect all rubrics going forward.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIndex(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={handleDeleteConfirm}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addDialogOpen}
        onClose={handleAddClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Field</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={newField.name}
              onChange={(e) =>
                setNewField((prev) => ({ ...prev, name: e.target.value }))
              }
              helperText="Internal identifier (e.g. opening_hook)"
            />
            <TextField
              label="Label"
              size="small"
              fullWidth
              value={newField.label}
              onChange={(e) =>
                setNewField((prev) => ({ ...prev, label: e.target.value }))
              }
              helperText="Display name shown to users"
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={newField.description}
              onChange={(e) =>
                setNewField((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <TextField
              label="Max Score"
              size="small"
              type="number"
              value={newField.score}
              onChange={(e) =>
                setNewField((prev) => ({
                  ...prev,
                  score: Number(e.target.value),
                }))
              }
              inputProps={{ min: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!isNewFieldValid || saving}
            onClick={handleAddConfirm}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RubricCard;
